import { MoldOrderType } from '../types/mold';

const bangkokDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Bangkok',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Return a UTC-midnight Date whose calendar portion is the Asia/Bangkok date
 * plus the requested number of days. Date-only form fields serialize via
 * toISOString(), so UTC midnight preserves the intended calendar date.
 */
export const getBangkokDateWithOffset = (
  offsetDays: number = 0,
  now: Date = new Date(),
): Date => {
  const parts = bangkokDateFormatter.formatToParts(now);
  const year = Number(parts.find(({ type }) => type === 'year')?.value);
  const month = Number(parts.find(({ type }) => type === 'month')?.value);
  const day = Number(parts.find(({ type }) => type === 'day')?.value);

  return new Date(Date.UTC(year, month - 1, day + offsetDays));
};

export const calculateDuration = (start: string, end: string): string => {
  if (!start || !end) return '';

  const startDate = new Date(start);
  const endDate = new Date(end);
  const durationMs = endDate.getTime() - startDate.getTime();
  const durationMinutes = Math.floor(durationMs / 60000);
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  if (hours === 0) {
    return `${minutes} นาที`;
  }

  return `${hours} ชม. ${minutes} นาที`;
};

export const formatDate = (date: Date): string => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatShortThaiDate = (date: Date): string => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatShortThaiDateFromString = (dateString: string): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return formatShortThaiDate(date);
};

export const formatDateFromString = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTimeFromString = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatShortDateTime = (dateString: string): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('th-TH', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getRemainingDaysInThai = (
  date: Date | string,
): { displayText: string; status: 'red' | 'yellow' | 'green' } => {
  if (!date) return { displayText: '', status: 'green' };
  const targetDate = new Date(date);
  if (isNaN(targetDate.getTime())) return { displayText: '', status: 'green' };
  const today = new Date();
  const todayStr = today.toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
  const targetDateStr = targetDate.toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
  const tomorrowStr = new Date(
    today.getTime() + 24 * 60 * 60 * 1000,
  ).toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
  const shortThaiDate = formatShortThaiDate(targetDate);

  if (targetDateStr === todayStr) {
    return { displayText: 'กำหนดส่งวันนี้', status: 'yellow' };
  }
  if (targetDateStr === tomorrowStr) {
    return { displayText: 'กำหนดส่งพรุ่งนี้', status: 'yellow' };
  }
  if (targetDate > today) {
    return { displayText: `กำหนดเสร็จ: ${shortThaiDate}`, status: 'green' };
  }
  return {
    displayText: `เลยกำหนด ${Math.abs(Math.ceil((targetDate.getTime() - today.getTime()) / (1000 * 3600 * 24)))} วัน`,
    status: 'red',
  };
};

/** Whole days elapsed since `date` (>= 0). */
export const getDaysSince = (date: Date | string): number => {
  const d = new Date(date);
  if (isNaN(d.getTime())) return 0;
  return Math.max(0, Math.floor((new Date().getTime() - d.getTime()) / (1000 * 3600 * 24)));
};

export const isProductionOrWaitingType = (type: MoldOrderType): boolean => {
  return [MoldOrderType.ALL_PRODUCTION, MoldOrderType.WAITING].includes(type);
};

export const isStoreOrderType = (type: MoldOrderType): boolean => {
  return (
    type.startsWith('maintenance.STORE') || type === MoldOrderType.ALL_STORE
  );
};

export const isTestingOrderType = (type: MoldOrderType): boolean => {
  return type.startsWith('maintenance.TESTING');
};

export const isAllStoreOrProductionType = (type: MoldOrderType): boolean => {
  return [MoldOrderType.ALL_STORE, MoldOrderType.ALL_PRODUCTION].includes(type);
};

export const getShiftLabel = (shift: string): string => {
  const shiftOptions = [
    { value: 'A', label: 'A (เช้า)' },
    { value: 'B', label: 'B (ดึก)' },
  ];
  const option = shiftOptions.find((opt) => opt.value === shift);
  return option ? option.label : shift;
};

export const formatThaiDate = (dateString: string): string => {
  const date = new Date(dateString);
  const thaiYear = date.getFullYear() + 543; // Convert to Buddhist year
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };
  return new Intl.DateTimeFormat('th-TH', options)
    .format(date)
    .replace(`${date.getFullYear()}`, `${thaiYear}`);
};

export const formatThaiDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const thaiYear = date.getFullYear() + 543;
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return new Intl.DateTimeFormat('th-TH', options)
    .format(date)
    .replace(`${date.getFullYear()}`, `${thaiYear}`);
};

export const getRelativeTimeString = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffTime / (1000 * 60));

  if (diffDays > 0) {
    return `${diffDays} วันที่แล้ว`;
  } else if (diffHours > 0) {
    return `${diffHours} ชั่วโมงที่แล้ว`;
  } else if (diffMinutes > 0) {
    return `${diffMinutes} นาทีที่แล้ว`;
  } else {
    return 'เมื่อสักครู่';
  }
};
